import activityTxsReduxDefinition from '@exodus/activity-txs/redux';
import analyticsReduxDefinition from '@exodus/analytics/redux';
import assetSourcesReduxDefinition from '@exodus/asset-sources/lib/redux';
import assetsReduxDefinition from '@exodus/assets-feature/redux';
import availableAssetsReduxDefinition from '@exodus/available-assets/redux';
import balancesReduxDefinition from '@exodus/balances/redux';
import accountStatesReduxDefinition from '@exodus/blockchain-metadata/redux/account-states';
import txLogsReduxDefinition from '@exodus/blockchain-metadata/redux/tx-logs';
import enabledAssetsReduxDefinition from '@exodus/enabled-assets/redux';
import exchangeReduxDefinition from '@exodus/exchange/lib/redux';
import feeDataReduxDefinition from '@exodus/fee-data-monitors/redux';
import fiatBalancesReduxDefinition from '@exodus/fiat-balances/redux';
import geolocationReduxDefinition from '@exodus/geolocation/redux';
import uiRedux from '@exodus/headless-react/redux';
import localeReduxDefinition from '@exodus/locale/redux';
import marketHistoryReduxDefinition from '@exodus/market-history/redux';
import nftsReduxDefinition from '@exodus/nfts/redux';
import ratesReduxDefinition from '@exodus/rates-monitor/redux';
import remoteConfigReduxDefinition from '@exodus/remote-config/lib/redux';
import restoreProgressReduxDefinition from '@exodus/restore-progress-tracker/redux';
import syncTimeReduxDefinition from '@exodus/sync-time/redux';
import uiConfigReduxDefinition from '@exodus/ui-config/redux';
import walletAccountsReduxDefinition from '@exodus/wallet-accounts/redux';
import ordersReduxDefinition from '@passkeys/orders/redux';
import walletReduxDefinition from '@passkeys/wallet-feature/redux';

import assetManagementReduxDefinition from '@/background/features/asset-management/redux';
import blinksReduxDefinition from '@/background/features/blinks/redux/redux';
import customTokensReduxDefinition from '@/background/features/custom-tokens/redux/redux';
import fiatRampReduxDefinition from '@/background/features/fiat-ramp/redux';
import playgroundSbtReduxDefinition from '@/background/features/playground-sbt/redux/index.js';
import pwaReduxDefinition from '@/background/features/pwa/redux/redux';
import testnetsReduxDefinition from '@/background/features/testnets/redux.js';
import type { NamedReduxModuleDefinition } from '@/types';
import modalReduxDefinition from '@/ui/flux/modal/index.js';
import isRestoringDefinition from './app/is-restoring.js';
import getIsFavoriteDefinition from './favorite-assets/get-is-favorite.js';
import removedTokensReduxDefinition from './removed-tokens/index.js';

interface ReduxDependenciesOpts {
  uiConfig: { [key: string]: any };
}

const createDependencies = (opts: ReduxDependenciesOpts) => [
  assetSourcesReduxDefinition,
  blinksReduxDefinition,
  customTokensReduxDefinition,
  exchangeReduxDefinition,
  fiatRampReduxDefinition(),
  modalReduxDefinition,
  playgroundSbtReduxDefinition,
  ratesReduxDefinition,
  remoteConfigReduxDefinition,
  removedTokensReduxDefinition,
  testnetsReduxDefinition(),
  assetManagementReduxDefinition(),
  pwaReduxDefinition,
  walletReduxDefinition,
  // The below redux modules are not typed and need a type assertion.
  accountStatesReduxDefinition as NamedReduxModuleDefinition<'accountStates'>,
  activityTxsReduxDefinition as NamedReduxModuleDefinition<'activityTxs'>,
  analyticsReduxDefinition as NamedReduxModuleDefinition<'analytics'>,
  assetsReduxDefinition as NamedReduxModuleDefinition<'assets'>, // Shipped types are outdated.
  availableAssetsReduxDefinition as NamedReduxModuleDefinition<'availableAssets'>,
  balancesReduxDefinition as NamedReduxModuleDefinition<'balances'>,
  enabledAssetsReduxDefinition as NamedReduxModuleDefinition<'enabledAssets'>,
  feeDataReduxDefinition as NamedReduxModuleDefinition<'feeData'>,
  fiatBalancesReduxDefinition({
    optimisticActivityEnabled: false,
  }) as NamedReduxModuleDefinition<'fiatBalances'>,
  geolocationReduxDefinition as NamedReduxModuleDefinition<'geolocation'>,
  localeReduxDefinition as NamedReduxModuleDefinition<'locale'>,
  marketHistoryReduxDefinition as NamedReduxModuleDefinition<'marketHistory'>,
  nftsReduxDefinition as NamedReduxModuleDefinition<'nfts'>,
  ordersReduxDefinition as NamedReduxModuleDefinition<'orders'>,
  restoreProgressReduxDefinition as NamedReduxModuleDefinition<'restoringAssets'>,
  syncTimeReduxDefinition as NamedReduxModuleDefinition<'time'>,
  txLogsReduxDefinition as NamedReduxModuleDefinition<'txLog'>,
  uiConfigReduxDefinition(
    opts.uiConfig,
  ) as NamedReduxModuleDefinition<'uiConfig'>,
  walletAccountsReduxDefinition() as NamedReduxModuleDefinition<'walletAccounts'>,
  uiRedux as NamedReduxModuleDefinition<'exodusUI'>,
  // Below definitions directly provide selectors.
  getIsFavoriteDefinition,
  isRestoringDefinition,
];

export default createDependencies;
